import React, { useCallback, useEffect, useMemo } from 'react'

import PropTypes from 'prop-types'

import {
  CheckCircleRounded as SuccessIcon,
  Close as CloseIcon,
  HighlightOff as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { IconButton, Snackbar, SnackbarContent } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { useSetState } from '~/src/Lib/Utils'

const displayName = 'Aroya/Snackbar'

const ICONS = {
  error: ErrorIcon,
  info: InfoIcon,
  success: SuccessIcon,
}

const useStyles = makeStyles(theme => ({
  contentRoot: {
    backgroundColor: theme.palette.background.paper,
    padding: '8px 26px 13px 18px',
    display: 'flex',
    alignItems: 'center',
  },
  contentAction: {
    position: 'inherit',
    marginRight: 0,
    paddingLeft: 0,
  },
  contentMessage: {
    marginRight: 30,
    maxWidth: 400,
    whiteSpace: 'pre-line'
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  infoStatusIcon: {
    color: theme.palette.appPrimary.main
  },
  successStatusIcon: {
    color: theme.palette.green.five,
  },
  errorStatusIcon: {
    color: theme.palette.error.main,
  },
  close: {
    width: '1rem',
    height: '1rem',
    padding: 0,
  },
  closeIcon: {
    color: theme.palette.contrast.disabled,
  },
}))

const getDefaultState = () => ({ snackbar: null, open: false })

const Component = ({
  clickaway = false,
  snackbarMessages,
  doDismissSnackbarMessage,
  ...restProps
}) => {
  const [{ snackbar, open }, setState] = useSetState(getDefaultState)
  const classes = useStyles(restProps)

  useEffect(() => {
    if (!snackbarMessages.length) return
    const [current, next] = snackbarMessages
    setState({ snackbar: current, open: Boolean(current && !next) })
  }, [setState, snackbarMessages])

  const onExited = useCallback(() => {
    const { key } = snackbar
    doDismissSnackbarMessage(key)
    setState({ snackbar: null, open: false })
  }, [snackbar, doDismissSnackbarMessage, setState])

  const onClose = useCallback((event, reason) => {
    if (reason === 'clickaway' && clickaway) {
      return
    }
    setState({ open: false })
  }, [clickaway, setState])

  const TransitionProps = useMemo(() => ({ onExited }), [onExited])

  if (!snackbar) return null

  const { key, message, status } = snackbar
  const { [status]: Icon } = ICONS

  return (
    <Snackbar
      key={key}
      open={open}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={5000}
      onClose={onClose}
      TransitionProps={TransitionProps}
    >
      <SnackbarContent
        aria-labelledby={key}
        message={(
          <span className={classes.message}>
            {Icon ? <Icon className={classes[`${status}StatusIcon`]} /> : null}
            <span id={key}>{message}</span>
          </span>
        )}
        action={[
          <IconButton
            key="close"
            aria-label="Close"
            className={classes.close}
            onClick={onClose}
            size="large"
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>,
        ]}
        classes={{
          root: classes.contentRoot,
          action: classes.contentAction,
          message: classes.contentMessage,
        }}
      />
    </Snackbar>
  )
}
Component.displayName = displayName
Component.propTypes = {
  snackbarMessages: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      message: PropTypes.node,
    })
  ).isRequired,
  doDismissSnackbarMessage: PropTypes.func.isRequired,
  clickaway: PropTypes.bool,
}

const Memoized = React.memo(Component)

export default Memoized
